import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import {
  RefreshCcw,
  Calendar,
  Search,
  Download,
  Filter,
  X,
  ChevronUp,
  ChevronDown,
  Save,


  Trash2,
  User as UserIcon,
  MapPin,
  FileText,
  Link as LinkIcon,
  ExternalLink,
  Check,
  Plus,
  CalendarOff,
} from 'lucide-react';
import {
  agendamentoAPI,
  type Agendamento,
  type AgendamentoFilters,
  type AgendamentoStatus,
  type Tecnico,
  type ChamadoERP,
  type SgpContrato,
  type AgendamentoBloqueio,
  type ResumoCidade,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { hasRole, userRoles } from '../utils/roles';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Field,
  FOCUS_RING,
  IconButton,
  Input,
  PageHeader,
  Pagination,
  Select,
  Spinner,
  StatusBadge,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Tabs,
  Textarea,
  Modal,
  EmptyState,
  MultiSelect,
  type Tone,
} from '../components/ui';

/**
 * Agendamento > Agenda — lista de agendamentos com filtros, busca,
 * sort, export CSV e modal de edição (atribuir técnico + observações
 * + status).
 *
 * Inspirado em Agendamento legado Admin/Agendamentos.js mas com
 * - Backend Mongo (não Google Sheets)
 * - Técnicos carregados de /agendamento/tecnicos (Fase 16a/e)
 * - Tema dark slate alinhado ao shell SAAS
 */

/** Tom semântico de cada status — o StatusBadge já cuida de cor + ícone. */
const STATUS_TONE: Record<AgendamentoStatus, Tone> = {
  pendente: 'warning',
  confirmado: 'primary',
  cancelado: 'neutral',
  realizado: 'success',
  pendente_encaixe: 'info',
  encaixe_rejeitado: 'neutral',
};

const STATUS_LABEL: Record<AgendamentoStatus, string> = {
  pendente: 'Pendente',
  confirmado: 'Confirmado',
  cancelado: 'Cancelado',
  realizado: 'Executado',
  pendente_encaixe: 'Encaixe pendente',
  encaixe_rejeitado: 'Encaixe rejeitado',
};

const PERIODO_LABEL: Record<Agendamento['periodo'], string> = {
  manha: 'Manhã',
  tarde: 'Tarde',
};

type SortKey = 'data' | 'cliente' | 'contrato' | 'cidade' | 'equipe' | 'status';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

/**
 * Tipo de serviço do agendamento — motivo da OS do ERP quando vinculada
 * na criação manual; docs antigos/página pública são instalação.
 */
function tipoServicoLabel(a: Agendamento): string {
  if (a.tipoServico?.trim()) return a.tipoServico;
  return a.consomeVaga === false ? 'OUTRO SERVIÇO' : 'INSTALAÇÃO DE KIT - 1ª CONEXÃO';
}

function errorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e.response?.data?.message ?? e.message ?? 'Erro desconhecido';
}

export default function Agendamentos() {
  const { user } = useAuth();
  const [items, setItems] = useState<Agendamento[]>([]);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Filtros iniciais podem vir da URL (cards do dashboard do Monitor —
  // ex: /agendamento/agenda?dataInicio=2026-07-14&dataFim=2026-07-14)
  const [filters, setFilters] = useState<AgendamentoFilters>(() => {
    const sp = new URLSearchParams(window.location.search);
    return {
      page: 1,
      limit: 100,
      dataInicio: sp.get('dataInicio') ?? undefined,
      dataFim: sp.get('dataFim') ?? undefined,
      status: sp.get('status') ?? undefined,
    };
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0,
    totalContratos: 0,
  });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'data',
    dir: 'desc',
  });
  const [showFilters, setShowFilters] = useState(
    () => !!(filters.dataInicio || filters.dataFim || filters.status),
  );
  const [editing, setEditing] = useState<Agendamento | null>(null);
  const [creating, setCreating] = useState(false);
  const [showBloqueios, setShowBloqueios] = useState(false);
  const [copied, setCopied] = useState(false);
  const [view, setView] = useState<'lista' | 'tecnicos'>('lista');

  const publicUrl = `${window.location.origin}/agendar`;
  function handleCopyPublicLink(): void {
    navigator.clipboard
      .writeText(publicUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        window.prompt('Copie o link manualmente:', publicUrl);
      });
  }

  // Monitor tem controle TOTAL do módulo agendamento (Fase 29).
  // canEdit: monitor OU acesso amplo (admin/legados). canDelete: só
  // admin/monitor — espelha o requireRole do backend.
  const canEdit = hasRole(user, 'monitor');
  const canDelete = userRoles(user).some((r) => r === 'admin' || r === 'monitor');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await agendamentoAPI.list(filters);
      setItems(r.data);
      setPagination({
        page: r.pagination.page,
        pages: r.pagination.pages,
        total: r.pagination.total,
        totalContratos: r.pagination.totalContratos ?? 0,
      });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  // Busca livre roda no SERVIDOR (Fase 31) — varre todas as páginas, não
  // só a carregada. Debounce pra não disparar request a cada tecla.
  useEffect(() => {
    const termo = search.trim();
    const t = setTimeout(() => {
      setFilters((f) =>
        (f.q ?? '') === termo ? f : { ...f, q: termo || undefined, page: 1 },
      );
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Carrega técnicos uma vez
  useEffect(() => {
    agendamentoAPI.tecnicos
      .list({ active: true })
      .then(setTecnicos)
      .catch(() => undefined);
  }, []);

  // Mini-dashboard de ocupação por cidade (Fase 31) — respeita o filtro de
  // data; sem filtro, mostra hoje. Refaz junto com a lista (dep em items)
  // pra refletir criações/edições na hora.
  const [resumo, setResumo] = useState<{
    dataInicio: string;
    dataFim: string;
    dias: number;
    cidades: ResumoCidade[];
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    agendamentoAPI
      .resumoCidades({
        dataInicio: filters.dataInicio,
        dataFim: filters.dataFim,
        tipoServico: filters.tipoServico,
        equipe: filters.equipe,
        cidade: filters.cidade,
      })
      .then((r) => {
        if (!cancelled) setResumo(r);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [filters.dataInicio, filters.dataFim, filters.tipoServico, filters.equipe, filters.cidade, items]);

  // Tipos de serviço distintos pro filtro (Fase 30)
  const [tipos, setTipos] = useState<string[]>([]);
  useEffect(() => {
    agendamentoAPI
      .tiposServico()
      .then(setTipos)
      .catch(() => undefined);
  }, []);

  // Cidades distintas pro filtro multi
  const [cidades, setCidades] = useState<string[]>([]);
  useEffect(() => {
    agendamentoAPI
      .cidades()
      .then(setCidades)
      .catch(() => undefined);
  }, []);

  // Seleções dos filtros multi (CSV nos filters — Fase 31)
  const statusSelecionados = useMemo(
    () => (filters.status ? filters.status.split(',').filter(Boolean) : []),
    [filters.status],
  );
  const tiposSelecionados = useMemo(
    () => (filters.tipoServico ? filters.tipoServico.split(',').filter(Boolean) : []),
    [filters.tipoServico],
  );
  const equipesSelecionadas = useMemo(
    () => (filters.equipe ? filters.equipe.split(',').filter(Boolean) : []),
    [filters.equipe],
  );

  const statusOptions = useMemo(
    () =>
      (Object.keys(STATUS_LABEL) as AgendamentoStatus[]).map((s) => ({
        value: s,
        label: STATUS_LABEL[s],
      })),
    [],
  );
  const tipoOptions = useMemo(
    () => [
      // Cobre docs com tipo "…DE KIT…" (1ª conexão / mudança de endereço)
      // e os antigos sem tipo gravado que consomem vaga. Ponto adicional,
      // verificações etc são tipos próprios listados abaixo.
      { value: '__instalacao__', label: 'INSTALAÇÃO DE KIT - 1ª CONEXÃO' },
      { value: '__outro__', label: 'Outro serviço (sem tipo)' },
      ...tipos
        .filter((t) => !/de kit/i.test(t))
        .map((t) => ({ value: t, label: t })),
    ],
    [tipos],
  );
  const cidadesSelecionadas = useMemo(
    () => (filters.cidade ? filters.cidade.split(',').filter(Boolean) : []),
    [filters.cidade],
  );
  const cidadeOptions = useMemo(() => {
    const set = new Set<string>(cidades);
    // Mantém selecionadas que não estão mais na lista
    for (const c of cidadesSelecionadas) set.add(c);
    return [...set]
      .sort((a, b) => a.localeCompare(b))
      .map((nome) => ({ value: nome, label: nome }));
  }, [cidades, cidadesSelecionadas]);

  const equipeOptions = useMemo(() => {
    const set = new Set<string>(['A DEFINIR']);
    for (const t of tecnicos) set.add(t.nome);
    // Mantém selecionadas que não estão na lista (ex: técnico desativado)
    for (const e of equipesSelecionadas) set.add(e);
    return [...set]
      .sort((a, b) => (a === 'A DEFINIR' ? -1 : b === 'A DEFINIR' ? 1 : a.localeCompare(b)))
      .map((nome) => ({ value: nome, label: nome }));
  }, [tecnicos, equipesSelecionadas]);

  // Ordenação da página atual (busca e filtros rodam no servidor)
  const filteredSorted = useMemo(() => {
    return [...items].sort((a, b) => {
      const dir = sort.dir === 'asc' ? 1 : -1;
      switch (sort.key) {
        case 'data':
          return (new Date(a.data).getTime() - new Date(b.data).getTime()) * dir;
        case 'cliente':
          return a.nomeCliente.localeCompare(b.nomeCliente) * dir;
        case 'contrato':
          return (a.contratoErpId - b.contratoErpId) * dir;
        case 'cidade':
          return a.cidade.localeCompare(b.cidade) * dir;
        case 'equipe':
          return a.equipe.localeCompare(b.equipe) * dir;
        case 'status':
          return a.status.localeCompare(b.status) * dir;
        default:
          return 0;
      }
    });
  }, [items, sort]);

  // Visão "Por técnico": agrupa por equipe (A DEFINIR por último), data asc
  // e, dentro do mesmo dia, manhã antes de tarde (Fase 31)
  const porTecnico = useMemo(() => {
    return [...filteredSorted].sort((a, b) => {
      const aIndef = a.equipe === 'A DEFINIR';
      const bIndef = b.equipe === 'A DEFINIR';
      if (aIndef !== bIndef) return aIndef ? 1 : -1;
      return (
        a.equipe.localeCompare(b.equipe) ||
        new Date(a.data).setHours(0, 0, 0, 0) - new Date(b.data).setHours(0, 0, 0, 0) ||
        (a.periodo === b.periodo ? 0 : a.periodo === 'manha' ? -1 : 1)
      );
    });
  }, [filteredSorted]);

  function toggleSort(key: SortKey): void {
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );
  }

  function handleExportCSV(): void {
    const rows = filteredSorted.map((a) => ({
      Data: formatDate(a.data),
      Periodo: PERIODO_LABEL[a.periodo],
      Cliente: a.nomeCliente,
      Contrato: a.contratoErpId,
      Plano: a.plano ?? '',
      TipoServico: tipoServicoLabel(a),
      ObservacaoAgenda: a.observacaoOperador ?? '',
      Bairro: a.bairro ?? '',
      Cidade: a.cidade,
      Equipe: a.equipe,
      Status: STATUS_LABEL[a.status],
      Observacoes: a.observacoes ?? '',
    }));
    downloadCSV(rows, `agendamentos-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <PageHeader
        icon={<Calendar size={22} />}
        title="Agenda"
        description={`${filteredSorted.length} de ${pagination.total} agendamento(s)`}
        actions={
          <>
            {canEdit && (
              <Button leadingIcon={<Plus size={15} />} onClick={() => setCreating(true)}>
                Novo agendamento
              </Button>
            )}
            {canEdit && (
              <Button
                variant="secondary"
                leadingIcon={<CalendarOff size={15} />}
                onClick={() => setShowBloqueios(true)}
                title="Bloquear dias na agenda"
              >
                Bloqueios
              </Button>
            )}
            <Button
              variant={copied ? 'outline' : 'secondary'}
              leadingIcon={copied ? <Check size={15} /> : <LinkIcon size={15} />}
              onClick={handleCopyPublicLink}
              title={publicUrl}
            >
              {copied ? 'Copiado!' : 'Link público'}
            </Button>
            <a href={publicUrl} target="_blank" rel="noopener noreferrer">
              <IconButton label="Abrir página pública em nova aba" tabIndex={-1}>
                <ExternalLink size={15} />
              </IconButton>
            </a>
            <Button
              variant="secondary"
              leadingIcon={<Download size={15} />}
              onClick={handleExportCSV}
              disabled={filteredSorted.length === 0}
            >
              CSV
            </Button>
            <Button
              variant={showFilters ? 'outline' : 'secondary'}
              leadingIcon={<Filter size={15} />}
              onClick={() => setShowFilters((v) => !v)}
            >
              Filtros
            </Button>
            <Button
              leadingIcon={<RefreshCcw size={15} className={loading ? 'animate-spin' : ''} />}
              onClick={() => void load()}
              disabled={loading}
            >
              Atualizar
            </Button>
          </>
        }
      />

      {/* Mini-dashboard: ocupação por cidade no período filtrado (Fase 31) */}
      {resumo && resumo.cidades.length > 0 && (
        <Card className="mb-4 overflow-hidden">
          <div className="px-4 pt-3 pb-2 flex items-baseline justify-between gap-2 flex-wrap">
            <h2 className="text-[15px] font-semibold tracking-[-0.015em] text-fg">
              Ocupação por cidade
            </h2>
            <span className="text-xs text-muted">
              {resumo.dataInicio === resumo.dataFim
                ? formatDate(`${resumo.dataInicio}T12:00:00.000Z`)
                : `${formatDate(`${resumo.dataInicio}T12:00:00.000Z`)} a ${formatDate(`${resumo.dataFim}T12:00:00.000Z`)}`}
              {!filters.dataInicio && !filters.dataFim && ' (hoje — filtre por data pra mudar)'}
            </span>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <Table bare>
              <THead>
                <TR>
                  <TH>Cidade</TH>
                  <TH align="center">Manhã</TH>
                  <TH align="center">Tarde</TH>
                  <TH align="center">Vagas livres</TH>
                </TR>
              </THead>
              <TBody>
                {resumo.cidades.map((c) => {
                  const livres =
                    c.vagasManha === null || c.vagasTarde === null
                      ? null
                      : c.vagasManha + c.vagasTarde;
                  return (
                    <TR key={c.cidade}>
                      <TD className="text-fg">{c.cidade}</TD>
                      <TD align="center" numeric>
                        {c.manha}
                      </TD>
                      <TD align="center" numeric>
                        {c.tarde}
                      </TD>
                      <TD align="center">
                        {livres === null ? (
                          <span className="text-faint" title="Cidade sem limite de vagas configurado">
                            —
                          </span>
                        ) : (
                          <span
                            className={
                              livres <= 0
                                ? 'font-semibold tabular-nums text-danger'
                                : 'font-semibold tabular-nums text-success'
                            }
                          >
                            {livres}
                            <span className="ml-1.5 text-[11px] font-normal text-muted">
                              M {c.vagasManha} · T {c.vagasTarde}
                            </span>
                          </span>
                        )}
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Abas de visão */}
      <Tabs<typeof view>
        className="mb-4"
        value={view}
        onChange={setView}
        items={[
          { value: 'lista', label: 'Agenda' },
          { value: 'tecnicos', label: 'Por técnico' },
        ]}
      />

      {/* Busca */}
      <Input
        className="mb-4"
        placeholder="Buscar por cliente, contrato, bairro ou cidade…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        leadingIcon={<Search size={15} />}
        trailingIcon={
          search ? (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="text-muted transition-colors hover:text-fg-2"
              aria-label="Limpar busca"
            >
              <X size={15} />
            </button>
          ) : undefined
        }
      />

      {/* Filtros */}
      {showFilters && (
        <Card className="mb-4 flex flex-wrap items-end gap-3 p-4">
          <FilterField label="Status">
            <MultiSelect
              options={statusOptions}
              selected={statusSelecionados}
              onChange={(sel) =>
                setFilters({
                  ...filters,
                  status: sel.length > 0 ? sel.join(',') : undefined,
                  page: 1,
                })
              }
              placeholder="Todos"
              plural="status"
            />
          </FilterField>
          <FilterField label="Período">
            <Select
              size="sm"
              value={filters.periodo ?? ''}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  periodo: (e.target.value || undefined) as Agendamento['periodo'] | undefined,
                  page: 1,
                })
              }
            >
              <option value="">Todos</option>
              <option value="manha">Manhã</option>
              <option value="tarde">Tarde</option>
            </Select>
          </FilterField>
          <FilterField label="Cidade">
            <MultiSelect
              options={cidadeOptions}
              selected={cidadesSelecionadas}
              onChange={(sel) =>
                setFilters({
                  ...filters,
                  cidade: sel.length > 0 ? sel.join(',') : undefined,
                  page: 1,
                })
              }
              placeholder="Todas"
              plural="cidades"
            />
          </FilterField>
          <FilterField label="De">
            <Input
              size="sm"
              type="date"
              value={filters.dataInicio ?? ''}
              onChange={(e) =>
                setFilters({ ...filters, dataInicio: e.target.value || undefined, page: 1 })
              }
            />
          </FilterField>
          <FilterField label="Até">
            <Input
              size="sm"
              type="date"
              value={filters.dataFim ?? ''}
              onChange={(e) =>
                setFilters({ ...filters, dataFim: e.target.value || undefined, page: 1 })
              }
            />
          </FilterField>
          <FilterField label="Tipo de serviço">
            <MultiSelect
              options={tipoOptions}
              selected={tiposSelecionados}
              onChange={(sel) =>
                setFilters({
                  ...filters,
                  tipoServico: sel.length > 0 ? sel.join(',') : undefined,
                  page: 1,
                })
              }
              placeholder="Todos"
              plural="tipos"
            />
          </FilterField>
          <FilterField label="Equipe">
            <MultiSelect
              options={equipeOptions}
              selected={equipesSelecionadas}
              onChange={(sel) =>
                setFilters({
                  ...filters,
                  equipe: sel.length > 0 ? sel.join(',') : undefined,
                  page: 1,
                })
              }
              placeholder="Todas"
              plural="equipes"
            />
          </FilterField>
          <div className="ml-auto flex items-center gap-3 self-center">
            <Badge tone="primary">
              Total filtrado: <strong>{pagination.total}</strong> agendamento(s) ·{' '}
              <strong>{pagination.totalContratos}</strong> contrato(s)
            </Badge>
            {(filters.status ||
              filters.periodo ||
              filters.cidade ||
              filters.dataInicio ||
              filters.dataFim ||
              filters.tipoServico ||
              filters.equipe ||
              filters.q) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch('');
                  setFilters({ page: 1, limit: filters.limit });
                }}
              >
                Limpar filtros
              </Button>
            )}
          </div>
        </Card>
      )}

      {error && (
        <Alert tone="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Tabela — visão Agenda */}
      {view === 'lista' && (
      <Table>
        <THead>
          <TR>
            <SortHeader label="Data" sortKey="data" sort={sort} onClick={toggleSort} />
            <TH>Período</TH>
            <SortHeader label="Cliente" sortKey="cliente" sort={sort} onClick={toggleSort} />
            <SortHeader label="Contrato" sortKey="contrato" sort={sort} onClick={toggleSort} />
            <TH>Plano</TH>
            <TH>Tipo de serviço</TH>
            <SortHeader label="Cidade" sortKey="cidade" sort={sort} onClick={toggleSort} />
            <SortHeader label="Equipe" sortKey="equipe" sort={sort} onClick={toggleSort} />
            <SortHeader label="Status" sortKey="status" sort={sort} onClick={toggleSort} />
          </TR>
        </THead>
        <TBody>
          {loading ? (
            <TR>
              <TD colSpan={9} align="center" className="py-12">
                <Spinner className="mx-auto" size={20} />
              </TD>
            </TR>
          ) : filteredSorted.length === 0 ? (
            <TR>
              <TD colSpan={9} align="center" className="py-12 text-muted">
                Nenhum agendamento encontrado.
              </TD>
            </TR>
          ) : (
            filteredSorted.map((ag) => (
              <TR key={ag.id} interactive={canEdit} onClick={() => canEdit && setEditing(ag)}>
                <TD className="whitespace-nowrap text-fg">{formatDate(ag.data)}</TD>
                <TD>{PERIODO_LABEL[ag.periodo]}</TD>
                <TD className="font-medium text-fg">{ag.nomeCliente}</TD>
                <TD numeric className="text-xs">
                  {ag.contratoErpId}
                </TD>
                <TD className="text-xs">{ag.plano || '—'}</TD>
                <TD className="max-w-[14rem] truncate text-xs" title={tipoServicoLabel(ag)}>
                  <span className={ag.consomeVaga === false ? 'text-info' : undefined}>
                    {tipoServicoLabel(ag)}
                  </span>
                </TD>
                <TD className="text-xs">
                  {ag.bairro && <span className="text-muted">{ag.bairro}, </span>}
                  {ag.cidade}
                </TD>
                <TD className="text-xs">{ag.equipe}</TD>
                <TD>
                  <StatusBadge tone={STATUS_TONE[ag.status]}>
                    {STATUS_LABEL[ag.status]}
                  </StatusBadge>
                </TD>
              </TR>
            ))
          )}
        </TBody>
      </Table>
      )}

      {/* Tabela — visão Por técnico (planilha) */}
      {view === 'tecnicos' && (
      <Table>
        <THead>
          <TR>
            <TH>Data</TH>
            <TH>Técnico</TH>
            <TH>Contrato</TH>
            <TH>Cliente</TH>
            <TH>Plano</TH>
            <TH>Tipo de serviço</TH>
            <TH>Período</TH>
            <TH>Observação</TH>
            <TH>Cidade</TH>
          </TR>
        </THead>
        <TBody>
          {loading ? (
            <TR>
              <TD colSpan={9} align="center" className="py-12">
                <Spinner className="mx-auto" size={20} />
              </TD>
            </TR>
          ) : porTecnico.length === 0 ? (
            <TR>
              <TD colSpan={9} align="center" className="py-12 text-muted">
                Nenhum agendamento encontrado.
              </TD>
            </TR>
          ) : (
            porTecnico.map((ag, i) => {
              const novoGrupo = i === 0 || porTecnico[i - 1]?.equipe !== ag.equipe;
              return (
                <Fragment key={ag.id}>
                  {novoGrupo && (
                    <TR className="bg-sunken">
                      <TD colSpan={9} className="py-2">
                        <span className="text-[13.5px] font-semibold text-primary">
                          {ag.equipe}
                        </span>
                        <span className="ml-2 text-xs text-muted">
                          {porTecnico.filter((x) => x.equipe === ag.equipe).length}{' '}
                          agendamento(s)
                        </span>
                      </TD>
                    </TR>
                  )}
                  <TR
                    interactive={canEdit}
                    onClick={() => canEdit && setEditing(ag)}
                    className={ag.status === 'cancelado' ? 'opacity-40 line-through' : undefined}
                  >
                    <TD className="whitespace-nowrap text-fg">{formatDate(ag.data)}</TD>
                    <TD className="text-xs">{ag.equipe}</TD>
                    <TD numeric className="text-xs">
                      {ag.contratoErpId}
                    </TD>
                    <TD className="text-xs font-medium text-fg">{ag.nomeCliente}</TD>
                    <TD className="text-xs">{ag.plano || '—'}</TD>
                    <TD className="max-w-[16rem] truncate text-xs" title={tipoServicoLabel(ag)}>
                      <span className={ag.consomeVaga === false ? 'text-info' : undefined}>
                        {tipoServicoLabel(ag)}
                      </span>
                    </TD>
                    <TD>{PERIODO_LABEL[ag.periodo]}</TD>
                    <TD
                      className="max-w-[14rem] truncate text-xs"
                      title={ag.observacaoOperador || undefined}
                    >
                      {ag.observacaoOperador ? (
                        /* Grifado estilo marca-texto — o recado do operador
                           precisa saltar aos olhos na lista do técnico */
                        <span className="bg-yellow-100 text-danger-solid font-semibold rounded px-1.5 py-0.5">
                          {ag.observacaoOperador}
                        </span>
                      ) : (
                        <span className="text-faint">—</span>
                      )}
                    </TD>
                    <TD className="text-xs">{ag.cidade}</TD>
                  </TR>
                </Fragment>
              );
            })
          )}
        </TBody>
      </Table>
      )}

      {pagination.pages > 1 && (
        <Pagination
          className="mt-4"
          page={pagination.page}
          pageCount={pagination.pages}
          total={pagination.total}
          pageSize={filters.limit}
          onChange={(page) => setFilters({ ...filters, page })}
        />
      )}

      {editing && (
        <DetailModal
          agendamento={editing}
          tecnicos={tecnicos}
          canDelete={canDelete}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}

      {creating && (
        <CreateModal
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void load();
          }}
          onReagendar={(ag) => {
            setCreating(false);
            setEditing(ag);
          }}
        />
      )}

      {showBloqueios && <BloqueiosModal onClose={() => setShowBloqueios(false)} />}
    </div>
  );
}

// ============================================================
// Modal de bloqueios de agenda (Fase 29)
// ============================================================

/**
 * Bloqueia agendamentos num dia+cidade (opcionalmente por tipo de serviço).
 * O dia fica sem vagas na página pública/prospect e a criação manual é
 * recusada com o motivo.
 */
function BloqueiosModal({ onClose }: { onClose: () => void }) {
  const [bloqueios, setBloqueios] = useState<AgendamentoBloqueio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState('');
  const [cidade, setCidade] = useState('');
  const [tipoServico, setTipoServico] = useState('');
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);

  async function load(): Promise<void> {
    setLoading(true);
    try {
      setBloqueios(await agendamentoAPI.bloqueios.list());
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleAdd(): Promise<void> {
    if (!data) {
      setError('Selecione a data do bloqueio.');
      return;
    }
    if (!cidade.trim()) {
      setError('Informe a cidade.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await agendamentoAPI.bloqueios.create({
        data,
        cidade: cidade.trim(),
        tipoServico: tipoServico.trim() || undefined,
        motivo: motivo.trim() || undefined,
      });
      setData('');
      setTipoServico('');
      setMotivo('');
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(b: AgendamentoBloqueio): Promise<void> {
    if (!window.confirm(`Remover o bloqueio de ${formatDate(b.data)} em ${b.cidade}?`)) return;
    try {
      await agendamentoAPI.bloqueios.remove(b.id);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      // Enter adiciona o bloqueio; clique fora não fecha (Fase 30b)
      onConfirm={() => {
        if (!saving) void handleAdd();
      }}
      title="Bloqueios de agenda"
      description="O dia bloqueado fica sem vagas na página pública e no app prospect, e a criação manual é recusada."
    >
      <div className="flex flex-col gap-5">
        {/* Novo bloqueio */}
        <Card className="bg-sunken p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Data" required>
              <Input type="date" value={data} onChange={(e) => setData(e.target.value)} />
            </Field>
            <Field label="Cidade" required>
              <Input
                value={cidade}
                onChange={(e) => setCidade(e.target.value.toUpperCase())}
                placeholder="ex: SOROCABA"
                className="uppercase"
              />
            </Field>
            <Field
              label="Tipo de serviço"
              hint="Vazio bloqueia todos os tipos."
              className="sm:col-span-2"
            >
              <Input
                value={tipoServico}
                onChange={(e) => setTipoServico(e.target.value)}
                placeholder="ex: instalação, verificação…"
              />
            </Field>
            <Field label="Motivo" hint="Opcional." className="sm:col-span-2">
              <Input
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="ex: feriado municipal, mutirão de rede…"
              />
            </Field>
          </div>
          <Button
            variant="danger"
            fullWidth
            className="mt-4"
            loading={saving}
            leadingIcon={<CalendarOff size={15} />}
            onClick={() => void handleAdd()}
          >
            Bloquear dia
          </Button>
        </Card>

        {error && <Alert tone="danger">{error}</Alert>}

        {/* Lista */}
        <div>
          <h3 className="mb-2 text-[11.5px] font-semibold uppercase tracking-[0.05em] text-muted">
            Bloqueios ativos ({bloqueios.length})
          </h3>
          {loading ? (
            <div className="flex justify-center py-6">
              <Spinner size={18} />
            </div>
          ) : bloqueios.length === 0 ? (
            <EmptyState icon={<CalendarOff size={20} />} title="Nenhum dia bloqueado" />
          ) : (
            <ul className="flex flex-col gap-2">
              {bloqueios.map((b) => (
                <li
                  key={b.id}
                  className="flex items-center gap-3 rounded-lg border border-line bg-sunken px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-[13.5px] font-medium text-fg">
                      {formatDate(b.data)} — {b.cidade}
                      <Badge tone={b.tipoServico ? 'warning' : 'danger'}>
                        {b.tipoServico || 'Todos os serviços'}
                      </Badge>
                    </div>
                    {b.motivo && (
                      <div className="mt-0.5 truncate text-xs text-muted">{b.motivo}</div>
                    )}
                  </div>
                  <IconButton
                    label="Remover bloqueio"
                    variant="ghost"
                    size="sm"
                    className="hover:bg-danger-soft hover:text-danger"
                    onClick={() => void handleRemove(b)}
                  >
                    <Trash2 size={15} />
                  </IconButton>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ============================================================
// Modal de criação manual (Fase 24)
// ============================================================

/**
 * Só instalação de kit (1ª conexão / mudança de endereço) debita o limite
 * de vagas da cidade — OS de outros tipos (verificação de irregularidade
 * etc) são executadas por outras equipes e o operador provisiona à parte.
 * Sem OS vinculada, assume instalação manual (consome).
 */
function osConsomeVaga(c?: ChamadoERP): boolean {
  if (!c) return true;
  const motivo = String(c.os_motivo_descricao ?? c.oc_tipo_descricao ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
  return motivo.includes('instala') || motivo.includes('mudanca de endere');
}

/**
 * Fluxo progressivo num único modal:
 *   1. Busca CPF/CNPJ no ERP (qualquer contrato — ativo ou inativo)
 *   2. Seleciona contrato → carrega automaticamente as OS abertas/em execução
 *   3. Seleciona OS (opcional) → escolhe data e período → salvar
 */
function CreateModal({
  onClose,
  onSaved,
  onReagendar,
}: {
  onClose: () => void;
  onSaved: () => void;
  onReagendar: (ag: Agendamento) => void;
}) {
  // Passo 1 — busca
  const [cpf, setCpf] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [contratos, setContratos] = useState<SgpContrato[] | null>(null);
  // Passo 2 — contrato + OS
  const [contrato, setContrato] = useState<SgpContrato | null>(null);
  const [chamados, setChamados] = useState<ChamadoERP[]>([]);
  // Agendamentos já existentes do contrato — trava de OS duplicada
  const [agsContrato, setAgsContrato] = useState<Agendamento[]>([]);
  const [loadingChamados, setLoadingChamados] = useState(false);
  const [chamadosErro, setChamadosErro] = useState<string | null>(null);
  const [selectedOsId, setSelectedOsId] = useState('');
  // Passo 3 — data/período
  const [dataAg, setDataAg] = useState('');
  const [periodo, setPeriodo] = useState<Agendamento['periodo']>('manha');
  const [cidade, setCidade] = useState('');
  const [bairro, setBairro] = useState('');
  const [consomeVaga, setConsomeVaga] = useState(true);
  const [vagas, setVagas] = useState<{ manha: number | null; tarde: number | null } | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleBuscar(): Promise<void> {
    const digits = cpf.replace(/\D/g, '');
    if (digits.length !== 11 && digits.length !== 14) {
      setError('Informe um CPF (11 dígitos) ou CNPJ (14 dígitos).');
      return;
    }
    setError(null);
    setBuscando(true);
    setContratos(null);
    setContrato(null);
    setSelectedOsId('');
    try {
      const r = await agendamentoAPI.buscarContrato(digits);
      setContratos(r.contratos ?? []);
      if (!r.contratos?.length) setError(r.msg || 'Nenhum contrato encontrado pra este documento.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBuscando(false);
    }
  }

  function handleSelectContrato(c: SgpContrato): void {
    setContrato(c);
    setCidade((c.endereco_cidade ?? '').toUpperCase());
    setBairro(c.endereco_bairro ?? '');
    setSelectedOsId('');
    setChamados([]);
    setChamadosErro(null);
    setError(null);
    // Agendamentos ativos do contrato (pra detectar OS já agendada)
    setAgsContrato([]);
    agendamentoAPI
      .list({ contratoErpId: c.contratoId, limit: 100 })
      .then((r) => setAgsContrato(r.data))
      .catch(() => undefined);
    // Busca automática das OS do contrato no ERP
    setLoadingChamados(true);
    agendamentoAPI.chamados
      .list(c.contratoId)
      .then((list) => {
        setChamados(list);
        const editaveis = list.filter(isOsEditavel);
        if (editaveis.length === 1 && editaveis[0]) {
          setSelectedOsId(String(editaveis[0].os_id));
        }
      })
      .catch((err) => setChamadosErro(errorMessage(err)))
      .finally(() => setLoadingChamados(false));
  }

  // Vagas do dia selecionado (informativo — operador pode passar por cima)
  useEffect(() => {
    if (!dataAg || !cidade.trim()) {
      setVagas(null);
      return;
    }
    let cancelled = false;
    agendamentoAPI.public
      .vagasNoMes(cidade.trim(), dataAg, dataAg)
      .then((r) => {
        if (!cancelled) setVagas(r.vagasPorData[dataAg] ?? null);
      })
      .catch(() => {
        if (!cancelled) setVagas(null);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAg, cidade]);

  const osEditaveis = chamados.filter(isOsEditavel);
  const chamadoSelecionado = osEditaveis.find((c) => String(c.os_id) === selectedOsId);
  const vagasPeriodo = vagas ? (periodo === 'manha' ? vagas.manha : vagas.tarde) : null;

  // OS selecionada já tem agendamento ativo? Oferece reagendar em vez de duplicar
  const agExistenteOs = selectedOsId
    ? agsContrato.find(
        (a) =>
          a.osErpId === Number(selectedOsId) &&
          (a.status === 'pendente' || a.status === 'confirmado'),
      )
    : undefined;

  // Deriva "consome vaga" do motivo da OS selecionada (operador pode sobrescrever)
  useEffect(() => {
    setConsomeVaga(osConsomeVaga(chamados.find((c) => String(c.os_id) === selectedOsId)));
  }, [selectedOsId, chamados]);

  async function handleCriar(): Promise<void> {
    if (!contrato) return;
    if (!dataAg) {
      setError('Selecione a data do agendamento.');
      return;
    }
    if (!cidade.trim()) {
      setError('Informe a cidade.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await agendamentoAPI.create({
        contratoErpId: contrato.contratoId,
        nomeCliente: contrato.razaoSocial,
        // Documento da busca: o ERP lista os serviços do contrato por CPF
        documentoCliente: cpf.replace(/\D/g, ''),
        cidade: cidade.trim(),
        bairro: bairro.trim() || undefined,
        plano: contrato.servico_plano || undefined,
        // Meio-dia UTC: evita o bug clássico de voltar 1 dia em BRT
        data: `${dataAg}T12:00:00.000Z`,
        periodo,
        osErpId: selectedOsId ? Number(selectedOsId) : undefined,
        consomeVaga,
        tipoServico:
          chamadoSelecionado?.os_motivo_descricao ||
          chamadoSelecionado?.oc_tipo_descricao ||
          undefined,
        protocoloUra: chamadoSelecionado?.oc_protocolo || undefined,
      });
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function contratoTone(c: SgpContrato): Tone {
    const s = (c.contratoStatusDisplay ?? '').toLowerCase();
    return s === 'ativo' || s.startsWith('ativo') ? 'success' : 'warning';
  }

  // Enter cria quando o formulário está pronto (o Modal liga Esc/Enter)
  const podeCriar = !saving && !!contrato && !!dataAg && !loadingChamados && !agExistenteOs;

  return (
    <Modal
      open
      onClose={onClose}
      onConfirm={() => {
        if (podeCriar) void handleCriar();
      }}
      title="Novo agendamento"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            leadingIcon={<Save size={15} />}
            loading={saving}
            onClick={() => void handleCriar()}
            disabled={!contrato || !dataAg || loadingChamados || !!agExistenteOs}
          >
            Criar agendamento
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {/* Passo 1 — Buscar cliente */}
        <Field label="CPF / CNPJ do cliente">
          <div className="flex gap-2">
            <Input
              inputMode="numeric"
              placeholder="Somente números"
              value={cpf}
              onChange={(e) => setCpf(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  // preventDefault: senão o Enter global do modal criaria
                  e.preventDefault();
                  void handleBuscar();
                }
              }}
            />
            <Button
              className="flex-none"
              leadingIcon={<Search size={15} />}
              loading={buscando}
              onClick={() => void handleBuscar()}
            >
              Buscar
            </Button>
          </div>
        </Field>

        {/* Passo 2 — Contratos (todos: ativos e inativos) */}
        {contratos && contratos.length > 0 && (
          <Field label={`Contrato (${contratos.length})`}>
            <div className="flex max-h-52 flex-col gap-2 overflow-y-auto pr-1">
              {contratos.map((c) => (
                <button
                  key={c.contratoId}
                  type="button"
                  onClick={() => handleSelectContrato(c)}
                  className={`w-full rounded-lg border px-3 py-2.5 text-left transition-colors duration-[120ms] ${FOCUS_RING} ${
                    contrato?.contratoId === c.contratoId
                      ? 'border-primary bg-primary-soft'
                      : 'border-line bg-sunken hover:border-faint'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-[13.5px] font-medium text-fg">
                      {c.contratoId} — {c.razaoSocial}
                    </span>
                    <Badge tone={contratoTone(c)}>{c.contratoStatusDisplay || '—'}</Badge>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted">
                    {[c.servico_plano, c.endereco_bairro, c.endereco_cidade]
                      .filter(Boolean)
                      .join(' · ') || 'Sem detalhes'}
                  </p>
                </button>
              ))}
            </div>
          </Field>
        )}

        {/* Passo 2b — OS do contrato no ERP */}
        {contrato && (
          <Field label="Ordem de Serviço no ERP">
            {loadingChamados ? (
              <div className="flex items-center gap-2 py-2 text-[13.5px] text-muted">
                <Spinner size={14} />
                Buscando OS abertas do contrato…
              </div>
            ) : chamadosErro ? (
              <Alert tone="danger">{chamadosErro}</Alert>
            ) : osEditaveis.length === 0 ? (
              <p className="py-1 text-xs text-muted">
                Nenhuma OS aberta ou em execução — o agendamento será criado só na
                agenda local.
              </p>
            ) : (
              <>
                <Select value={selectedOsId} onChange={(e) => setSelectedOsId(e.target.value)}>
                  <option value="">Sem OS vinculada</option>
                  {osEditaveis.map((c) => (
                    <option key={String(c.os_id)} value={String(c.os_id)}>
                      {c.os_id} - {c.os_motivo_descricao || c.oc_tipo_descricao || 'OS'}
                      {c.os_status_descricao ? ` (${c.os_status_descricao})` : ''}
                    </option>
                  ))}
                </Select>
                {agExistenteOs && (
                  <Alert
                    tone="warning"
                    className="mt-2"
                    action={
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => onReagendar(agExistenteOs)}
                      >
                        Reagendar
                      </Button>
                    }
                  >
                    A OS {selectedOsId} já tem agendamento em {formatDate(agExistenteOs.data)} (
                    {PERIODO_LABEL[agExistenteOs.periodo]}) com {agExistenteOs.equipe}.
                  </Alert>
                )}
              </>
            )}
          </Field>
        )}

        {/* Passo 3 — Data e período */}
        {contrato && !loadingChamados && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Data" required>
                <Input type="date" value={dataAg} onChange={(e) => setDataAg(e.target.value)} />
              </Field>
              <Field label="Período">
                <Select
                  value={periodo}
                  onChange={(e) => setPeriodo(e.target.value as Agendamento['periodo'])}
                >
                  <option value="manha">Manhã</option>
                  <option value="tarde">Tarde</option>
                </Select>
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Cidade">
                <Input
                  value={cidade}
                  onChange={(e) => setCidade(e.target.value.toUpperCase())}
                  placeholder="ex: SOROCABA"
                  className="uppercase"
                />
              </Field>
              <Field label="Bairro">
                <Input value={bairro} onChange={(e) => setBairro(e.target.value)} />
              </Field>
            </div>

            <Field hint="Instalação de kit / mudança de endereço consomem vaga. OS de outros tipos (verificação etc) são de outras equipes e não debitam o limite.">
              <Checkbox
                label="Consome vaga da cidade"
                checked={consomeVaga}
                onChange={(e) => setConsomeVaga(e.target.checked)}
              />
            </Field>

            {dataAg && vagas && (
              <Alert
                tone={
                  consomeVaga && vagasPeriodo !== null && vagasPeriodo <= 0
                    ? 'warning'
                    : 'neutral'
                }
              >
                Vagas em {cidade} — manhã: {vagas.manha ?? 'sem limite'}, tarde:{' '}
                {vagas.tarde ?? 'sem limite'}.
                {!consomeVaga ? (
                  <> Este agendamento não debita vaga.</>
                ) : (
                  vagasPeriodo !== null &&
                  vagasPeriodo <= 0 && (
                    <> Período esgotado — o agendamento será criado mesmo assim.</>
                  )
                )}
              </Alert>
            )}
          </>
        )}

        {error && <Alert tone="danger">{error}</Alert>}
      </div>
    </Modal>
  );
}

// ============================================================
// Modal de detalhes (edição)
// ============================================================

/**
 * Template pré-escrito do campo observações — vai pro ERP como
 * os_servico_prestado quando uma OS é selecionada (Fase 23).
 */
const OBS_TEMPLATE = `GPON:
SINAL:
NOME DA REDE:
SENHA WI-FI:
RESPONSÁVEL:`;

/** OS aberta (0) ou em execução (1) — as únicas que podem ser reagendadas. */
function isOsEditavel(c: ChamadoERP): boolean {
  if (c.os_id === '' || c.os_id === null || c.os_id === undefined) return false;
  if (c.os_status === 0 || c.os_status === 1) return true;
  const desc = String(c.os_status_descricao ?? '').toLowerCase();
  return desc.includes('abert') || desc.includes('execu');
}

function DetailModal({
  agendamento,
  tecnicos,
  canDelete,
  onClose,
  onSaved,
}: {
  agendamento: Agendamento;
  tecnicos: Tecnico[];
  canDelete: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [equipe, setEquipe] = useState(agendamento.equipe);
  // Serviços do contrato no ERP (Fase 32) — internet + adicionais. Começa
  // com o plano gravado e é substituído pela lista viva quando chega.
  const [planos, setPlanos] = useState<string[]>(
    agendamento.plano ? [agendamento.plano] : [],
  );
  const [observacoes, setObservacoes] = useState(agendamento.observacoes?.trim() || OBS_TEMPLATE);
  // Recado de logística exibido na visão "Por técnico" (Fase 31)
  const [obsOperador, setObsOperador] = useState(agendamento.observacaoOperador ?? '');
  const [status, setStatus] = useState<AgendamentoStatus>(agendamento.status);
  // Reagendamento pelo modal (Fase 22)
  const [dataEdit, setDataEdit] = useState(() => {
    const d = new Date(agendamento.data);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [periodoEdit, setPeriodoEdit] = useState<Agendamento['periodo']>(agendamento.periodo);
  const [horaEdit, setHoraEdit] = useState(agendamento.periodo === 'tarde' ? '13:00' : '08:00');
  // OS do ERP (Fase 23)
  const [chamados, setChamados] = useState<ChamadoERP[]>([]);
  const [loadingChamados, setLoadingChamados] = useState(true);
  const [chamadosErro, setChamadosErro] = useState<string | null>(null);
  const [selectedOsId, setSelectedOsId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carrega chamados/OS do contrato no ERP ao abrir o modal
  useEffect(() => {
    let cancelled = false;
    agendamentoAPI.chamados
      .list(agendamento.contratoErpId)
      .then((list) => {
        if (cancelled) return;
        setChamados(list);
        // Pré-seleciona a OS vinculada na criação manual (Fase 24) ou,
        // na falta dela, a única OS editável
        const editaveis = list.filter(isOsEditavel);
        const vinculada = agendamento.osErpId
          ? editaveis.find((c) => Number(c.os_id) === agendamento.osErpId)
          : undefined;
        const os = vinculada ?? (editaveis.length === 1 ? editaveis[0] : undefined);
        if (os) {
          setSelectedOsId(String(os.os_id));
          // Se a OS já tem serviço prestado preenchido no ERP e as
          // observações locais ainda são o template puro, carrega o do ERP
          const erpTexto = (os.os_servicoprestado ?? '').trim();
          if (erpTexto) {
            setObservacoes((prev) => (prev.trim() === OBS_TEMPLATE ? erpTexto : prev));
          }
        }
      })
      .catch((err) => {
        if (!cancelled) setChamadosErro(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoadingChamados(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agendamento.contratoErpId, agendamento.osErpId]);

  // Serviços do contrato no ERP (Fase 32) — mostra "internet + adicionais".
  // Silencioso: falhou, fica o plano gravado que já está na tela.
  useEffect(() => {
    let cancelled = false;
    agendamentoAPI
      .planosContrato(agendamento.id)
      .then((r) => {
        if (!cancelled && r.planos.length > 0) setPlanos(r.planos);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [agendamento.id]);

  const osEditaveis = chamados.filter(isOsEditavel);

  // Sugere apenas técnicos que atendem essa cidade (ou que atendem todas)
  const tecnicosFiltrados = tecnicos.filter(
    (t) => t.cidades.length === 0 || t.cidades.includes(agendamento.cidade),
  );
  const tecnicoSelecionado = tecnicosFiltrados.find((t) => t.nome === equipe);

  async function handleSave(): Promise<void> {
    setError(null);
    if (!dataEdit) {
      setError('Data inválida.');
      return;
    }
    if (selectedOsId && !tecnicoSelecionado) {
      setError('Selecione um técnico cadastrado pra atualizar a OS no ERP.');
      return;
    }
    setSaving(true);
    try {
      // 1. Atualiza a OS no ERP primeiro (se selecionada) — se falhar,
      //    nada é salvo e o operador vê o erro.
      if (selectedOsId && tecnicoSelecionado) {
        await agendamentoAPI.chamados.updateOs(selectedOsId, {
          tecnicoResponsavel: tecnicoSelecionado.username || tecnicoSelecionado.nome,
          dataAgendamento: `${dataEdit} ${horaEdit}`,
          servicoPrestado: observacoes.trim(),
        });
      }

      // 2. Persiste no Mongo (Agenda local)
      await agendamentoAPI.update(agendamento.id, {
        equipe: equipe.trim() || 'A DEFINIR',
        observacoes: observacoes.trim(),
        observacaoOperador: obsOperador.trim(),
        status,
        // Meio-dia UTC: evita o bug clássico de voltar 1 dia em BRT
        data: `${dataEdit}T12:00:00.000Z`,
        periodo: periodoEdit,
      });
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (
      !window.confirm(
        `Excluir agendamento de ${agendamento.nomeCliente} em ${formatDate(agendamento.data)}?`,
      )
    )
      return;
    setDeleting(true);
    try {
      await agendamentoAPI.remove(agendamento.id);
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      onConfirm={() => {
        if (!saving && !deleting) void handleSave();
      }}
      title="Detalhes do agendamento"
      footer={
        <>
          {canDelete && (
            <Button
              variant="outline"
              className="mr-auto border-danger-line text-danger hover:bg-danger-soft"
              leadingIcon={<Trash2 size={15} />}
              loading={deleting}
              disabled={saving}
              onClick={() => void handleDelete()}
            >
              Excluir
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} disabled={saving || deleting}>
            Cancelar
          </Button>
          <Button
            leadingIcon={<Save size={15} />}
            loading={saving}
            disabled={deleting}
            onClick={() => void handleSave()}
          >
            Salvar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {/* Info do agendamento (read-only) */}
        <div className="flex flex-col gap-1.5">
          <InfoRow icon={<UserIcon size={14} />} label="Cliente" value={agendamento.nomeCliente} />
          <InfoRow icon={<FileText size={14} />} label="Contrato" value={agendamento.contratoErpId} />
          <InfoRow
            icon={<Calendar size={14} />}
            label="Data"
            value={`${formatDate(agendamento.data)} (${PERIODO_LABEL[agendamento.periodo]})`}
          />
          <InfoRow
            icon={<MapPin size={14} />}
            label="Local"
            value={`${agendamento.bairro ? agendamento.bairro + ', ' : ''}${agendamento.cidade}`}
          />
          {planos.length > 0 && (
            <InfoRow
              icon={<FileText size={14} />}
              label="Plano"
              value={planos.join(' + ')}
              highlight
            />
          )}
          <InfoRow
            icon={<FileText size={14} />}
            label="Serviço"
            value={tipoServicoLabel(agendamento)}
          />

          {agendamento.protocoloUra && (
            <InfoRow
              icon={<FileText size={14} />}
              label="Protocolo URA"
              value={agendamento.protocoloUra}
            />
          )}
        </div>

        {/* Edição */}
        <div className="flex flex-col gap-4 border-t border-line pt-5">
          {/* OS do ERP (Fase 23) */}
          <Field
            label="Ordem de Serviço no ERP"
            hint={
              selectedOsId
                ? `Ao salvar, a OS ${selectedOsId} será reagendada no ERP com a data/horário/técnico abaixo.`
                : undefined
            }
          >
            {loadingChamados ? (
              <div className="flex items-center gap-2 py-2 text-[13.5px] text-muted">
                <Spinner size={14} />
                Buscando OS do contrato no ERP…
              </div>
            ) : chamadosErro ? (
              <Alert tone="danger">{chamadosErro}</Alert>
            ) : osEditaveis.length === 0 ? (
              <p className="py-1 text-xs text-muted">
                Nenhuma OS aberta ou em execução pra este contrato — o salvar
                atualiza só a agenda local.
              </p>
            ) : (
              <Select value={selectedOsId} onChange={(e) => setSelectedOsId(e.target.value)}>
                <option value="">Não atualizar OS no ERP</option>
                {osEditaveis.map((c) => (
                  <option key={String(c.os_id)} value={String(c.os_id)}>
                    {c.os_id} - {c.os_motivo_descricao || c.oc_tipo_descricao || 'OS'}
                    {c.os_status_descricao ? ` (${c.os_status_descricao})` : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {/* Reagendar (Fase 22/23) */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Data">
              <Input type="date" value={dataEdit} onChange={(e) => setDataEdit(e.target.value)} />
            </Field>
            <Field label="Período">
              <Select
                value={periodoEdit}
                onChange={(e) => {
                  const p = e.target.value as Agendamento['periodo'];
                  setPeriodoEdit(p);
                  // Ajusta o horário default junto com o período
                  setHoraEdit(p === 'tarde' ? '13:00' : '08:00');
                }}
              >
                <option value="manha">Manhã</option>
                <option value="tarde">Tarde</option>
              </Select>
            </Field>
            <Field label="Horário">
              <Input type="time" value={horaEdit} onChange={(e) => setHoraEdit(e.target.value)} />
            </Field>
          </div>

          <Field label="Status">
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as AgendamentoStatus)}
            >
              <option value="pendente">Pendente</option>
              <option value="confirmado">Confirmado</option>
              <option value="realizado">Executado</option>
              <option value="cancelado">Cancelado</option>
              {(status === 'pendente_encaixe' || status === 'encaixe_rejeitado') && (
                <option value={status}>{STATUS_LABEL[status]}</option>
              )}
            </Select>
          </Field>

          <Field
            label="Técnico / Equipe"
            hint={
              <>
                {tecnicosFiltrados.length > 0
                  ? `${tecnicosFiltrados.length} técnico(s) disponível(eis) pra ${agendamento.cidade}.`
                  : 'Cadastre técnicos em Configurações pra usar select.'}
                {selectedOsId && tecnicoSelecionado && (
                  <span className="text-primary">
                    {' '}
                    → ERP: {tecnicoSelecionado.username || tecnicoSelecionado.nome}
                    {!tecnicoSelecionado.username &&
                      ' (sem username do ERP — sincronize em Configurações)'}
                  </span>
                )}
              </>
            }
          >
            {tecnicosFiltrados.length > 0 ? (
              <Select value={equipe} onChange={(e) => setEquipe(e.target.value)}>
                <option value="A DEFINIR">A DEFINIR</option>
                {tecnicosFiltrados.map((t) => (
                  <option key={t.id} value={t.nome}>
                    {t.nome} ({t.equipe})
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                value={equipe}
                onChange={(e) => setEquipe(e.target.value)}
                placeholder="Nome do técnico ou equipe"
              />
            )}
          </Field>

          <Field
            label="Observação da agenda"
            hint={'Recado de logística — aparece na coluna Observação da visão "Por técnico". Não vai pro ERP.'}
          >
            <Input
              value={obsOperador}
              onChange={(e) => setObsOperador(e.target.value)}
              placeholder="ex: ir antes das 14h, ligar ao chegar…"
              maxLength={200}
            />
          </Field>

          <Field
            label="Observações"
            hint={selectedOsId ? 'Enviado ao ERP como serviço prestado da OS.' : undefined}
          >
            <Textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={6}
              placeholder="Anotações sobre o serviço (ex: GPON, SINAL, REDE, SENHA…)"
              className="font-mono"
            />
          </Field>

          {error && <Alert tone="danger">{error}</Alert>}
        </div>
      </div>
    </Modal>
  );
}

// ============================================================
// Helpers
// ============================================================

function SortHeader({
  label,
  sortKey,
  sort,
  onClick,
}: {
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  onClick: (k: SortKey) => void;
}) {
  const active = sort.key === sortKey;
  return (
    <TH
      className="cursor-pointer select-none transition-colors hover:text-fg-2"
      onClick={() => onClick(sortKey)}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active &&
          (sort.dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
      </span>
    </TH>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[12.5px] font-medium text-fg-2">{label}</span>
      {children}
    </label>
  );
}

function InfoRow({
  icon,
  label,
  value,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  /** Grifado com fundo amarelo — usado no Plano (Fase 32) */
  highlight?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="text-primary flex-shrink-0">{icon}</span>
      <span className="text-xs uppercase tracking-wide text-muted w-24 flex-shrink-0">
        {label}
      </span>
      {highlight ? (
        <span className="bg-yellow-100 text-danger-solid font-semibold rounded px-1.5 py-0.5 break-all">
          {value}
        </span>
      ) : (
        <span className="text-fg break-all">{value}</span>
      )}
    </div>
  );
}

function downloadCSV(rows: Array<Record<string, string | number>>, filename: string): void {
  if (rows.length === 0) return;
  const first = rows[0]!;
  const headers = Object.keys(first);
  const escape = (v: unknown): string => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const lines = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(',')),
  ];
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

